import { useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import logo from "../assets/logo.PNG";
import ph from "../assets/placeholder.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; height: 100%; overflow: hidden; user-select: none; }
`;

const fadeUp = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

// ─── Shell ────────────────────────────────────────────────────────────────────

const Screen = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: #f8f7fc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// ─── Scroll body ──────────────────────────────────────────────────────────────

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  padding-bottom: 24px;
`;

// ─── Hero image ───────────────────────────────────────────────────────────────

const HeroWrap = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 0;
  padding: 54px 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8f7fc;
`;
const HeroThumb = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 16px;
`;
const HeroImg = styled.div`
  width: 100%;
  height: 100%;
  background:
    linear-gradient(
      180deg,
      rgba(116, 4, 190, 0.25) 0%,
      rgba(10, 0, 16, 0.45) 100%
    ),
    url(${ph}) center/cover no-repeat;
`;

const HeroLogo = styled.img`
  width: 36px;
  opacity: 0.9;
  margin-bottom: 14px;
`;
const HeroTitle = styled.h1`
  font-family: "Mendl", sans-serif;
  font-size: 26px;
  font-weight: 600;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.02em;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 6px;
  animation: ${fadeUp} 0.5s ease both;
`;
const HeroSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: rgba(116, 4, 190, 0.65);
  text-align: center;
  letter-spacing: 0.04em;
  line-height: 1.5;
  margin-bottom: 16px;
  animation: ${fadeUp} 0.6s ease both;
`;

const PlayBtn = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s ease;
  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;
const PlayTriangle = styled.div`
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 16px solid rgba(255, 255, 255, 0.9);
  margin-left: 4px;
`;
const VideoCaption = styled.div`
  padding: 10px 18px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const VideoCaptionDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7404be, #a855fd);
  flex-shrink: 0;
`;
const VideoCaptionText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 300;
  font-style: italic;
  color: rgba(116, 4, 190, 0.7);
  letter-spacing: 0.04em;
`;

// ─── Content ──────────────────────────────────────────────────────────────────

const Content = styled.div`
  padding: 20px 18px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 2px 12px rgba(116, 4, 190, 0.07),
    0 1px 3px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} ${({ $i }) => 0.2 + ($i || 0) * 0.07}s ease both;
`;

const SectionHeader = styled.div`
  padding: 14px 18px 10px;
  border-bottom: 1px solid rgba(168, 85, 253, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
`;
const SectionIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;
const SectionTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.03em;
`;

const SectionBody = styled.div`
  padding: 12px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Crisis card
const CrisisCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(116, 4, 190, 0.04),
    rgba(168, 85, 253, 0.06)
  );
  border: 1px solid rgba(168, 85, 253, 0.14);
  border-radius: 12px;
  padding: 12px 14px;
`;
const CrisisName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 4px;
  letter-spacing: 0.01em;
`;
const CrisisContact = styled.a`
  display: block;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #7404be;
  letter-spacing: 0.02em;
  text-decoration: none;
  margin-bottom: 3px;
`;
const CrisisDesc = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

// List items
const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 2px 0;
`;
const ListDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7404be, #a855fd);
  flex-shrink: 0;
  margin-top: 5px;
`;
const ListText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #333;
  line-height: 1.55;
  letter-spacing: 0.01em;
`;

// Resource cards
const ResourceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(116, 4, 190, 0.04);
  border: 1px solid rgba(168, 85, 253, 0.12);
  border-radius: 10px;
  padding: 10px 12px;
`;
const ResourceDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7404be, #a855fd);
  flex-shrink: 0;
`;
const ResourceText = styled.div``;
const ResourceName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
  letter-spacing: 0.01em;
  margin-bottom: 1px;
`;
const ResourceSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.01em;
`;

// Safety note
const SafetyNote = styled.div`
  background: linear-gradient(135deg, #fff0f3, #fce8ff);
  border: 1px solid rgba(242, 53, 110, 0.2);
  border-radius: 14px;
  padding: 16px;
  text-align: center;
  animation: ${fadeUp} 0.6s ease both;
`;
const SafetyIcon = styled.div`
  font-size: 28px;
  margin-bottom: 8px;
`;
const SafetyTitle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 700;
  font-style: italic;
  color: #c0003c;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
`;
const SafetyText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: #555;
  line-height: 1.65;
  letter-spacing: 0.01em;
`;
const SafetyDivider = styled.div`
  width: 60%;
  height: 1px;
  margin: 10px auto;
  background: rgba(242, 53, 110, 0.15);
`;

// ─── Nav (same pill pattern) ──────────────────────────────────────────────────

const NavBar = styled.nav`
  position: relative;
  z-index: 10;
  width: 100%;
  flex-shrink: 0;
  padding: 10px 20px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f7fc;
`;
const NavPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 64px;
  border-radius: 100px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(#3d2253, #1f0e2a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.22),
    0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
    0 8px 32px rgba(0, 0, 0, 0.45);
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14%;
    right: 14%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;
const NavActiveBg = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $index, $count }) =>
    `calc(${(($index + 0.5) / $count) * 100}% - 28px)`};
  transform: translateY(-50%);
  width: 56px;
  height: 46px;
  border-radius: 20px;
  background: rgba(168, 85, 253, 0.22);
  border: 1px solid rgba(168, 85, 253, 0.35);
  pointer-events: none;
`;
const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;
  font-family: "Mendl", sans-serif;
  font-size: 9.5px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  letter-spacing: 0.04em;
  -webkit-tap-highlight-color: transparent;
  &:active {
    opacity: 0.7;
  }
`;
const NavDot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a855fd;
  margin-top: -1px;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconMatches = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconFlare = () => (
  <svg viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
  </svg>
);
const IconMsg = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconHelp = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ─── Main ─────────────────────────────────────────────────────────────────────

// ─── Founder & About Sections ─────────────────────────────────────────────────

const FounderCard = styled.div`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 2px 12px rgba(116, 4, 190, 0.07),
    0 1px 3px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} 0.55s ease both;
`;
const FounderHeader = styled.div`
  padding: 16px 18px 12px;
  border-bottom: 1px solid rgba(168, 85, 253, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
`;
const FounderIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;
const FounderHeaderTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.03em;
`;
const FounderQuoteWrap = styled.div`
  padding: 16px 18px 18px;
  background: linear-gradient(
    135deg,
    rgba(116, 4, 190, 0.03),
    rgba(168, 85, 253, 0.05)
  );
`;
const FounderQuoteMark = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 64px;
  font-weight: 600;
  line-height: 0.6;
  color: rgba(116, 4, 190, 0.18);
  margin-bottom: 6px;
`;
const FounderQuote = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  font-style: italic;
  color: #333;
  line-height: 1.75;
  letter-spacing: 0.01em;
`;
const FounderName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #7404be;
  letter-spacing: 0.04em;
  margin-top: 14px;
`;

const AboutCard = styled.div`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 2px 12px rgba(116, 4, 190, 0.07),
    0 1px 3px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} 0.6s ease both;
`;
const AboutHeader = styled.div`
  padding: 16px 18px 12px;
  border-bottom: 1px solid rgba(168, 85, 253, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
`;
const AboutIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
`;
const AboutHeaderTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.03em;
`;
const AboutBody = styled.div`
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const AboutPara = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #333;
  line-height: 1.75;
  letter-spacing: 0.01em;
`;
const AboutDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 253, 0.2),
    transparent
  );
`;

const SecuritySummaryCard = styled.div`
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 2px 12px rgba(116, 4, 190, 0.07),
    0 1px 3px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} 0.55s ease both;
  padding: 18px 18px 16px;
`;
const SecuritySummaryTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const SecuritySummaryText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #444;
  line-height: 1.75;
  letter-spacing: 0.01em;
  margin-bottom: 14px;
`;
const ReadMoreBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 600;
  font-style: italic;
  color: #7404be;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.15s ease;
  &:active {
    opacity: 0.7;
  }
`;

const SecurityBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.25s ease both;
`;
const SecuritySheet = styled.div`
  width: 100%;
  max-height: 88vh;
  background: #fff;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  overflow: hidden;
`;
const SecuritySheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.12);
  margin: 12px auto 0;
  flex-shrink: 0;
`;
const SecuritySheetHeader = styled.div`
  padding: 16px 20px 14px;
  border-bottom: 1px solid rgba(168, 85, 253, 0.1);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SecuritySheetTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: italic;
  color: #1a1a2e;
  letter-spacing: 0.02em;
`;
const SecuritySheetClose = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const SecuritySheetScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  padding: 20px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const SecurityBlockTitle = styled.h3`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-style: italic;
  color: #7404be;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
`;
const SecurityQuoteWrap = styled.div`
  background: linear-gradient(
    135deg,
    rgba(116, 4, 190, 0.04),
    rgba(168, 85, 253, 0.07)
  );
  border-left: 3px solid rgba(116, 4, 190, 0.4);
  border-radius: 0 12px 12px 0;
  padding: 14px 16px;
`;
const SecurityQuoteMark = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 48px;
  font-weight: 600;
  line-height: 0.6;
  color: rgba(116, 4, 190, 0.2);
  margin-bottom: 6px;
`;
const SecurityQuote = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  font-style: italic;
  color: #333;
  line-height: 1.75;
  letter-spacing: 0.01em;
`;
const SecurityFounderName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #7404be;
  letter-spacing: 0.04em;
  margin-top: 12px;
`;
const SecurityPara = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #333;
  line-height: 1.75;
  letter-spacing: 0.01em;
`;
const SecurityDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 253, 0.2),
    transparent
  );
`;

export default function HelpPage({ onNavigate }) {
  const [showSecurity, setShowSecurity] = useState(false);
  return (
    <>
      <GlobalStyle />
      <Screen>
        <Body>
          {/* Hero */}
          <HeroWrap>
            <HeroLogo src={logo} alt="" />
            <HeroTitle>Help & Resources</HeroTitle>
            <HeroSub>
              Support for Sexual Health, Addiction Recovery & Trauma Healing
            </HeroSub>
            <HeroThumb>
              <HeroImg />
              <PlayBtn>
                <PlayTriangle />
              </PlayBtn>
            </HeroThumb>
            <VideoCaption>
              <VideoCaptionDot />
              <VideoCaptionText>A video message from our CEO</VideoCaptionText>
            </VideoCaption>
          </HeroWrap>

          <Content>
            {/* Crisis Support */}
            <Section $i={0}>
              <SecuritySummaryCard>
                <SecuritySummaryTitle>
                  🔒 Built on Security. Driven by Trust.
                </SecuritySummaryTitle>
                <SecuritySummaryText>
                  NudR was built by a team with 40+ years of combined
                  cybersecurity experience — from end-to-end encryption to
                  real-time threat monitoring. Our founder Launa has over a
                  decade in cybersecurity that led her to develop a platform
                  where everyone can connect without fear. We don't just react
                  to risks; we anticipate them.
                </SecuritySummaryText>
                <ReadMoreBtn onClick={() => setShowSecurity(true)}>
                  Click to read more about our Security →
                </ReadMoreBtn>
              </SecuritySummaryCard>
              <br />
              <SectionHeader>
                <SectionIcon>🆘</SectionIcon>
                <SectionTitle>Crisis Support</SectionTitle>
              </SectionHeader>
              <SectionBody>
                <CrisisCard>
                  <CrisisName>National Sexual Assault Hotline</CrisisName>
                  <CrisisContact href="tel:18006564673">
                    📞 1-800-656-4673
                  </CrisisContact>
                  <CrisisDesc>
                    24/7 confidential support for survivors of sexual assault
                  </CrisisDesc>
                </CrisisCard>
                <CrisisCard>
                  <CrisisName>Crisis Text Line</CrisisName>
                  <CrisisContact>💬 Text HOME to 741741</CrisisContact>
                  <CrisisDesc>24/7 crisis support via text</CrisisDesc>
                </CrisisCard>
                <CrisisCard>
                  <CrisisName>National Suicide Prevention Lifeline</CrisisName>
                  <CrisisContact href="tel:988">
                    📞 Call or text 988
                  </CrisisContact>
                  <CrisisDesc>
                    24/7 suicide prevention and crisis support
                  </CrisisDesc>
                </CrisisCard>
              </SectionBody>
            </Section>

            {/* When to Seek Help */}
            <Section $i={1}>
              <SectionHeader>
                <SectionIcon>🤝</SectionIcon>
                <SectionTitle>When to Seek Help</SectionTitle>
              </SectionHeader>
              <SectionBody>
                {[
                  "Sexual behavior causing relationship problems",
                  "Inability to control sexual urges",
                  "Engaging in risky sexual behavior",
                  "Depression, anxiety, or suicidal thoughts",
                  "History of sexual trauma impacting daily life",
                  "Sexual behavior interfering with work or school",
                  "Using sex to cope with emotions",
                  "Concern expressed by friends or family",
                ].map((t, i) => (
                  <ListItem key={i}>
                    <ListDot />
                    <ListText>{t}</ListText>
                  </ListItem>
                ))}
              </SectionBody>
            </Section>

            {/* Warning Signs */}
            <Section $i={2}>
              <SectionHeader>
                <SectionIcon>⚠️</SectionIcon>
                <SectionTitle>Warning Signs of Sex Addiction</SectionTitle>
              </SectionHeader>
              <SectionBody>
                {[
                  "Loss of control despite consequences",
                  "Preoccupation with sexual thoughts",
                  "Escalation to riskier behavior",
                  "Shame or guilt after sexual activity",
                  "Lying or secrecy around sexual behavior",
                  "Neglecting responsibilities",
                ].map((t, i) => (
                  <ListItem key={i}>
                    <ListDot />
                    <ListText>{t}</ListText>
                  </ListItem>
                ))}
              </SectionBody>
            </Section>

            {/* Sex Addiction Resources */}
            <Section $i={3}>
              <SectionHeader>
                <SectionIcon>💜</SectionIcon>
                <SectionTitle>Sex Addiction Support Resources</SectionTitle>
              </SectionHeader>
              <SectionBody>
                {[
                  {
                    name: "Sex Addicts Anonymous (SAA)",
                    sub: "12-step recovery",
                  },
                  {
                    name: "Society for the Advancement of Sexual Health (SASH)",
                    sub: "Professional resources",
                  },
                  {
                    name: "Sexaholics Anonymous (SA)",
                    sub: "Peer recovery fellowship",
                  },
                  {
                    name: "Sex and Love Addicts Anonymous (SLAA)",
                    sub: "12-step program",
                  },
                ].map((r, i) => (
                  <ResourceCard key={i}>
                    <ResourceDot />
                    <ResourceText>
                      <ResourceName>{r.name}</ResourceName>
                      <ResourceSub>{r.sub}</ResourceSub>
                    </ResourceText>
                  </ResourceCard>
                ))}
              </SectionBody>
            </Section>

            {/* Trauma Recovery */}
            <Section $i={4}>
              <SectionHeader>
                <SectionIcon>🌱</SectionIcon>
                <SectionTitle>Trauma and Abuse Recovery</SectionTitle>
              </SectionHeader>
              <SectionBody>
                {[
                  {
                    name: "National Center for PTSD",
                    sub: "Trauma education and support",
                  },
                  {
                    name: "EMDR International Association",
                    sub: "Therapist directory",
                  },
                  {
                    name: "National Child Traumatic Stress Network",
                    sub: "Childhood trauma resources",
                  },
                ].map((r, i) => (
                  <ResourceCard key={i}>
                    <ResourceDot />
                    <ResourceText>
                      <ResourceName>{r.name}</ResourceName>
                      <ResourceSub>{r.sub}</ResourceSub>
                    </ResourceText>
                  </ResourceCard>
                ))}
              </SectionBody>
            </Section>

            {/* Security Summary + expandable sheet */}

            {/* Security full-screen sheet */}
            {showSecurity && (
              <SecurityBackdrop onClick={() => setShowSecurity(false)}>
                <SecuritySheet onClick={(e) => e.stopPropagation()}>
                  <SecuritySheetHandle />
                  <SecuritySheetHeader>
                    <SecuritySheetTitle>
                      Our Security & Story
                    </SecuritySheetTitle>
                    <SecuritySheetClose onClick={() => setShowSecurity(false)}>
                      ✕
                    </SecuritySheetClose>
                  </SecuritySheetHeader>
                  <SecuritySheetScroll>
                    <div>
                      <SecurityBlockTitle>
                        A Message From Our Founder & CEO
                      </SecurityBlockTitle>
                      <SecurityQuoteWrap>
                        <SecurityQuoteMark>"</SecurityQuoteMark>
                        <SecurityQuote>
                          Hi, I'm Launa, the founder of NudR. For over a decade,
                          I've worked in cybersecurity, and for 20 years, I've
                          been immersed in the tech world. As a woman, I know
                          how vulnerable we can feel in digital spaces and how
                          hard it is to trust. That's why I built NudR. I wanted
                          a platform where women and everyone could explore
                          relationships without fear, knowing they are protected
                          every step.
                          <br />
                          <br />
                          Building NudR is more than just a dream. It is a
                          business grounded in security. Our team's collective
                          40+ years of cybersecurity experience are the backbone
                          of this app. From day one, we designed NudR with a
                          security-first mindset. Every feature we built, from
                          profile encryption to real-time monitoring, rests on a
                          robust foundation. We don't just react to risks; we
                          anticipate them and constantly refine our safeguards.
                          Our mission is simple: we believe everyone deserves a
                          dating space that is safe, empowering, and free from
                          exploitation. We don't just help people connect; we
                          give them peace of mind, knowing their well-being is
                          our priority.
                        </SecurityQuote>
                        <SecurityFounderName>
                          — Launa, Founder & CEO of NudR
                        </SecurityFounderName>
                      </SecurityQuoteWrap>
                    </div>

                    <SecurityDivider />

                    <div>
                      <SecurityBlockTitle>More About Us</SecurityBlockTitle>
                      <SecurityPara>
                        NudR was born from a personal mission. After years
                        navigating the tech world, we realized that the
                        traditional dating landscape was not built with security
                        and trust in mind. So we set out to change that. Every
                        step of NudR's journey reflects Lana's vision: a space
                        where technology, empowerment, and safety come together.
                      </SecurityPara>
                    </div>
                    <SecurityDivider />
                    <SecurityPara>
                      Our team has developed decades of combined experience in
                      cybersecurity and technology. From building secure
                      infrastructures to designing intuitive user experiences,
                      we approach every aspect of NudR with precision. Our
                      collective background ensures that every feature is built
                      atop a foundation of trust and innovation.
                    </SecurityPara>
                    <SecurityDivider />
                    <SecurityPara>
                      At NudR, security is not an afterthought; it is our core.
                      From the ground up, we architected every layer using
                      end-to-end encryption, real-time anomaly detection, and
                      proactive threat analysis. Our users can trust that their
                      data, identities, and interactions are safeguarded by
                      best-in-class technology.
                    </SecurityPara>
                    <SecurityDivider />
                    <SecurityPara>
                      Our mission extends deeper than just technology. We
                      believe that every person deserves a safe, open
                      environment to explore themselves authentically. By
                      connecting people on their terms, we reduce risks, foster
                      healthy exploration, and provide pathways to professional
                      support. As we grow, so does our commitment to safety, to
                      innovation, and to every person who walks through our
                      digital doors.
                    </SecurityPara>
                    <SecurityDivider />
                    <SecurityPara>
                      We invite you to join us today. Download NudR, create your
                      account, and begin your journey with confidence. If you
                      have any questions or concerns, we're here for you. Our
                      team is always ready to help. Your safety and security are
                      our top priority, and we want you to feel secure,
                      empowered, and supported every step of the way.
                    </SecurityPara>
                  </SecuritySheetScroll>
                </SecuritySheet>
              </SecurityBackdrop>
            )}

            {/* Safety Note */}
            <SafetyNote>
              <SafetyIcon>🚨</SafetyIcon>
              <SafetyTitle>Important Safety Note</SafetyTitle>
              <SafetyDivider />
              <SafetyText>
                If you are in{" "}
                <strong style={{ color: "#c0003c" }}>immediate danger</strong>{" "}
                or experiencing suicidal thoughts, call emergency services
                immediately.
              </SafetyText>
              <SafetyDivider />
              <SafetyText style={{ fontSize: 11, color: "#888" }}>
                These resources are informational and do not replace
                professional medical or mental health care.
              </SafetyText>
            </SafetyNote>
          </Content>
        </Body>

        {/* Bottom nav */}
        <NavBar>
          <NavPill>
            <NavActiveBg $index={4} $count={5} />
            <NavItem onClick={() => onNavigate?.("matches")}>
              <IconMatches />
              <span>Matches</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("discover")}>
              <IconFlare />
              <span>Discover</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("messages")}>
              <IconMsg />
              <span>Messages</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("profile")}>
              <IconUser />
              <span>Profile</span>
            </NavItem>
            <NavItem $active>
              <IconHelp />
              <span>Help</span>
              <NavDot />
            </NavItem>
          </NavPill>
        </NavBar>
      </Screen>
    </>
  );
}
